import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, GitCompare, Bolt, BatteryCharging, FileText, ShieldCheck, Weight } from "lucide-react";
import { KEY_SPEC_KEYS } from "../data/products";

const KEY_ICONS = {
  motor: Bolt,
  voltage: BatteryCharging,
  brakingSystem: ShieldCheck,
  maxLoading: Weight,
};

export const ProductCard = ({ product, index, actions, productLabels, onOpen, onRegister, onCompare }) => {
  const [activeImage, setActiveImage] = useState(0);
  const reverse = index % 2 === 1;

  return (
    <motion.article
      initial={{ opacity: 0, y: 54 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.65, ease: "easeOut" }}
      data-testid={`product-card-${product.id}`}
      className="grid lg:grid-cols-2 gap-5 sm:gap-8 lg:gap-14 items-center"
    >
      <div className={`relative ${reverse ? "lg:order-2" : ""}`}>
        <div className="image-stage product-stage rounded-lg p-1.5 sm:p-2 aspect-[1.2/1] sm:aspect-[4/3] flex items-center justify-center overflow-hidden">
          <button
            onClick={() => onOpen(product)}
            data-testid={`product-image-${product.id}`}
            className="relative block h-full w-full group"
            aria-label={`View ${product.name} details`}
          >
            <img
              src={product.images[activeImage]}
              alt={`evLUXE ${product.name} electric scooter in ${
                product.colorNames?.[activeImage] || "selected colour"
              }`}
              loading="lazy"
              className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-[1.018]"
            />
          </button>
        </div>

        {product.images.length > 1 && (
          <div className="mt-2.5 sm:mt-3">
            <div className="mb-2 flex items-center justify-between gap-3 text-xs">
              <span className="text-white/50">{actions.availableColours}</span>
              <span className="font-mono text-brand">{product.colorNames?.[activeImage]}</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {product.images.map((img, i) => (
                <button
                  key={img}
                  type="button"
                  onClick={() => setActiveImage(i)}
                  aria-pressed={activeImage === i}
                  aria-label={`${product.name} in ${product.colorNames?.[i] || `colour ${i + 1}`}`}
                  title={product.colorNames?.[i]}
                  className={`relative h-16 sm:h-20 rounded-lg border bg-white/[0.025] p-1.5 sm:p-2 transition-colors ${
                    activeImage === i ? "border-brand" : "border-white/10 hover:border-white/35"
                  }`}
                >
                  <img src={img} alt="" className="h-full w-full object-contain" loading="lazy" />
                  <span
                    aria-hidden="true"
                    className={`absolute bottom-1.5 left-1/2 h-1 w-8 -translate-x-1/2 rounded-full transition-colors ${
                      activeImage === i ? "bg-brand" : "bg-white/20"
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className={reverse ? "lg:order-1" : ""}>
        <p className="font-mono text-xs text-brand mb-2 sm:mb-3">
          {product.series} / 0{index + 1}
        </p>
        <h3 className="font-display font-bold text-3xl sm:text-4xl">{product.name}</h3>
        <p className="mt-2 sm:mt-3 text-brand/90 text-sm">{product.tagline}</p>
        <p className="mt-3 sm:mt-4 text-white/65 leading-relaxed max-w-xl">{product.description}</p>

        <div className="mt-5 sm:mt-7 grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3">
          {KEY_SPEC_KEYS.map((k) => {
            const Icon = KEY_ICONS[k];
            return (
              <div key={k} className="glass rounded-lg p-3">
                <Icon size={16} className="text-brand mb-1.5" />
                <div className="text-[10px] uppercase text-white/45">
                  {productLabels[k]}
                </div>
                <div className="text-xs text-white/85 mt-0.5 font-mono leading-tight">
                  {product.specs[k]}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-5 sm:mt-7 grid grid-cols-[1fr_3rem_3rem] gap-2 sm:flex sm:flex-wrap">
          <button
            onClick={() => onRegister(product.name)}
            data-testid={`card-register-${product.id}`}
            className="btn-glow inline-flex min-w-0 items-center justify-center gap-2 bg-brand text-black font-semibold px-4 sm:px-6 py-3 rounded-full text-sm"
          >
            {actions.registerInterest} <ArrowRight size={16} />
          </button>
          <button
            onClick={() => onCompare(product.id)}
            data-testid={`card-compare-${product.id}`}
            aria-label={`Compare ${product.name}`}
            className="inline-flex h-12 sm:h-auto items-center justify-center gap-2 border border-white/20 hover:border-brand/60 text-white px-0 sm:px-6 sm:py-3 rounded-full text-sm transition-colors"
          >
            <GitCompare size={16} /> <span className="hidden sm:inline">{actions.compareModel}</span>
          </button>
          <button
            onClick={() => onOpen(product)}
            data-testid={`card-viewspecs-${product.id}`}
            aria-label={`View ${product.name} specifications`}
            className="inline-flex h-12 sm:h-auto items-center justify-center gap-2 border border-white/20 hover:border-brand/60 text-white/80 hover:text-brand px-0 sm:px-4 sm:py-3 rounded-full text-sm transition-colors"
          >
            <FileText size={16} /> <span className="hidden sm:inline">{actions.viewSpecs}</span>
          </button>
        </div>
      </div>
    </motion.article>
  );
};

export default ProductCard;
